import {Component, computed, inject, OnInit, signal, viewChild} from '@angular/core';
import {Card} from 'primeng/card';
import {TaInfoCardComponent} from '../../components/ta-info-card/ta-info-card.component';
import {TabsModule} from 'primeng/tabs';
import {Badge} from 'primeng/badge';
import {AllSubmissionsSearchComponent} from '../../components/ta-list-tables/all-submissions-search/all-submissions-search.component';
import {
  AdminService,
  BasicTaDetails,
  FinanceDashboardCounts,
  FullTaDetails,
  SubmissionApproversDetails,
} from '../../../api';
import {ConfirmationWrapperService} from '../../../services/confirmation-wrapper.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {FinanceRequiresActionListComponent} from '../../components/ta-list-tables/finance-requires-action-list/finance-requires-action-list.component';
import {FinancePaidInAdvanceListComponent} from '../../components/ta-list-tables/finance-paid-in-advance-list/finance-paid-in-advance-list.component';
import {OverdueApprovalsListComponent} from '../../components/ta-list-tables/overdue-approvals-list/overdue-approvals-list.component';
import {TaListBase} from '../ta-list-base.directive';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {TaCardAction} from '../../../util/app.types';
import {SplitButton} from 'primeng/splitbutton';
import {MenuItem} from 'primeng/api';
import {HttpResponse} from '@angular/common/http';

// The tab scenarios the server understands for the finance dashboard.
type FinanceDashboardScenario = 'payment' | 'prepaid' | 'stale';

// All finance dashboard tabs - the server scenarios plus the client only search tab.
type FinanceDashboardTab = FinanceDashboardScenario | 'searchAll';

interface FinanceDashboardTabOption {
  value: FinanceDashboardTab;
  label: string;
  countKey?: keyof FinanceDashboardCounts;
}

const FinanceDashboardTabOptions: FinanceDashboardTabOption[] = [
  {value: 'payment', label: 'Ready for Finalisation', countKey: 'payment'},
  {value: 'prepaid', label: 'Prepayment Requests', countKey: 'prepaid'},
  {value: 'stale', label: 'Overdue Approvals', countKey: 'stale'},
  {value: 'searchAll', label: 'Search All'},
];

@Component({
  selector: 'clc-finance-dashboard-page',
  imports: [
    Card,
    TaInfoCardComponent,
    TabsModule,
    Badge,
    AllSubmissionsSearchComponent,
    FinanceRequiresActionListComponent,
    FinancePaidInAdvanceListComponent,
    OverdueApprovalsListComponent,
    ScrollHeightDirective,
    SplitButton,
  ],
  templateUrl: './clc-finance-dashboard-page.html',
  styleUrl: './clc-finance-dashboard-page.scss',
})
export class ClcFinanceDashboardPage extends TaListBase implements OnInit {
  private confirmationServiceWrapper = inject(ConfirmationWrapperService);
  private adminApi = inject(AdminService);
  private errorMessageService = inject(ErrorMessageService);

  tabOption = signal<FinanceDashboardTab>('payment');
  counts = signal<FinanceDashboardCounts>({});
  exporting = signal(false);

  // The main button exports only prepayments due by today (CLC-153).
  exportMenu: MenuItem[] = [
    {label: 'Include all prepayments', command: () => this.exportBusinessCentral(true)},
  ];

  // Results are cached per tab scenario, so revisiting a tab does not refetch.
  private tabResults = signal<Partial<Record<FinanceDashboardScenario, BasicTaDetails[]>>>({});

  requiresFinalisationList = computed(() => this.tabResults()['payment'] ?? []);
  paidInAdvanceList = computed(() => this.tabResults()['prepaid'] ?? []);
  overdueApprovalsList = computed(() => this.tabResults()['stale'] ?? []);

  // Prepaid tab only: submission ID → scheduled payment day (CLC-153).
  private prePaymentDates = signal<Record<string, string>>({});
  scheduledPaymentDate = computed(() => {
    const id = this.selectedTa()?.id;
    return this.tabOption() === 'prepaid' && id ? this.prePaymentDates()[id] : undefined;
  });

  // Tab panels are hidden rather than destroyed, so the lists keep their ticks across tab switches.
  private requiresActionList = viewChild(FinanceRequiresActionListComponent);
  private paidInAdvanceListComponent = viewChild(FinancePaidInAdvanceListComponent);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    this.store.updateBreadCrumbData({
      title: 'Finance Dashboard',
      titleIcon: 'fa-regular fa-dollar-sign fa-lg',
    });
    const initialTab = this.tabOption();
    if (this.isScenarioTab(initialTab)) {
      this.loadTab(initialTab);
    }
  }

  onTabChange(tab: FinanceDashboardTab): void {
    this.tabOption.set(tab);
    this.resetSelectedTa();
    this.requiresActionList()?.clearSelection();
    this.paidInAdvanceListComponent()?.clearSelection();
    if (this.isScenarioTab(tab)) {
      this.loadTab(tab);
    }
  }

  // Only the three server backed tabs are loaded - 'searchAll' does its own searching.
  private isScenarioTab(tab: FinanceDashboardTab): tab is FinanceDashboardScenario {
    return tab !== 'searchAll';
  }

  tabCount(tab: FinanceDashboardTabOption): number | undefined {
    return tab.countKey ? this.counts()[tab.countKey] : undefined;
  }

  loadTab(scenario: FinanceDashboardScenario, force: boolean = false): void {
    if (!force && !!this.tabResults()[scenario]) {
      return;
    }
    this.loadingService.showLoading({message: 'Loading Finance TAs', targetElement: this.loadingRef});
    this.adminApi.getSubmissionsForFinanceDashboard(scenario).subscribe({
      next: resp => {
        if (!!resp.data) {
          this.tabResults.update(results => ({...results, [scenario]: resp.data?.results ?? []}));
          if (scenario === 'prepaid') {
            this.prePaymentDates.set(resp.data.prePaymentDates ?? {});
          }
          if (!!resp.data.counts) {
            this.counts.set(resp.data.counts);
          }
        } else {
          console.error('Null finance submissions data', resp);
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      },
    });
  }

  // An action taken on one tab can change the counts on the others, so drop every
  // cached result and refetch the tab that is currently open.
  reloadAfterAction(): void {
    const currentTab = this.tabOption();
    this.tabResults.set({});
    if (this.isScenarioTab(currentTab)) {
      this.loadTab(currentTab, true);
    }
  }

  taActionClicked(action: TaCardAction, taSubmission?: FullTaDetails): void {
    switch (action) {
      case 'travelApprove':
        this.approveTaClicked(taSubmission);
        break;
      case 'finalise':
        this.finaliseTaClicked(taSubmission);
        break;
      case 'prepayment':
        this.processPrepaymentTaClicked(taSubmission);
        break;
      case 'acquittalApprove':
        this.approveAcquittalTaClicked(taSubmission);
        break;
      case 'cancelApprove':
        this.approveCancellationTaClicked(taSubmission);
        break;
      case 'cancelFinalise':
        this.finaliseCancellationTaClicked(taSubmission);
        break;
    }
  }

  // Approving the travel itself, not the acquittal - reachable from the overdue approvals and
  // search tabs, where a PendingApproval TA can be selected. The server lets a finance admin
  // action the pending Manager/Funding approval even though they are not the assigned approver.
  approveTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      this.confirmationServiceWrapper.approveConfirm('approve', '', taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        // The server refuses when there is no pending Manager/Funding approval to action, so this
        // is reachable for a TA the finance admin can see but not approve - say so rather than
        // closing the dialog and appearing to have done nothing.
        error: err => {
          console.error('Error approving TA: ', err);
          this.errorMessageService.showTemporaryErrorWithTitle(
            'Approval Failed', 'This TA could not be approved. It may have no approval pending for you to action.', 8000);
        },
      });
    }
  }

  approveAcquittalTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      this.confirmationServiceWrapper.approveAcquittalConfirm('approve', '', taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        error: err => {
          console.log(err);
        },
      });
    }
  }

  finaliseTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      // The payment tab lists approved cancellations alongside approved acquittals - both finalise
      // through the same endpoint, but the cancellation gets its own wording.
      if (taSubmission.status === 'CancelApproved') {
        this.finaliseCancellationTaClicked(taSubmission);
        return;
      }
      this.confirmationServiceWrapper.finaliseConfirm('finalise', '', taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        error: err => {
          console.log(err);
        },
      });
    }
  }

  // Signing off the cancellation of a pre-paid TA whose prepayment was already processed. Finance
  // admins hold global ManageTaSubmission, which the server accepts in place of being the assigned
  // cancellation approver (TAStore.ApproveCancellationSubmission).
  approveCancellationTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      this.confirmationServiceWrapper.approveCancellationConfirm('', taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        error: err => {
          console.log(err);
        },
      });
    }
  }

  // Closing out an approved cancellation - the same finalise endpoint as finaliseTaClicked, which
  // the server routes by status (AdminController.FinaliseSubmission).
  finaliseCancellationTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      this.confirmationServiceWrapper.finaliseCancellationConfirm('', taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        error: err => {
          console.log(err);
        },
      });
    }
  }

  // Batch actions over the rows ticked on a tab. The server skips anything it cannot action and the
  // datasource reports that, so success and partial success both just reload the tab.
  finaliseSelectedTasClicked(taSubmissions: BasicTaDetails[]): void {
    if (taSubmissions.length === 0) {
      return;
    }
    this.confirmationServiceWrapper.finaliseBatchConfirm(taSubmissions).subscribe({
      next: () => {
        this.resetSelectedTa();
        this.reloadAfterAction();
      },
      error: err => {
        console.error('Error finalising selected TAs: ', err);
      },
    });
  }

  processSelectedPrepaymentsClicked(taSubmissions: BasicTaDetails[]): void {
    if (taSubmissions.length === 0) {
      return;
    }
    this.confirmationServiceWrapper.processPrepaymentBatchConfirm(taSubmissions).subscribe({
      next: () => {
        this.resetSelectedTa();
        this.reloadAfterAction();
      },
      error: err => {
        console.error('Error processing selected prepayments: ', err);
      },
    });
  }

  processPrepaymentTaClicked(taSubmission?: FullTaDetails): void {
    if (taSubmission) {
      this.confirmationServiceWrapper.processPrepaymentConfirm(taSubmission).subscribe({
        next: () => {
          this.resetSelectedTa();
          this.reloadAfterAction();
        },
        error: err => {
          console.log(err);
        },
      });
    }
  }

  overrideApproversClicked(details: SubmissionApproversDetails): void {
    this.loadingService.showLoading({message: 'Updating Approvers', targetElement: this.loadingRef});
    // The datasource reports any failure - only a successful override changes what the tabs show.
    this.taSubmissionDatasource.overrideApprovers(details).subscribe({
      next: resp => {
        this.loadingService.hideLoading();
        if (resp?.success) {
          this.reloadAfterAction();
        }
      },
      error: () => {
        this.loadingService.hideLoading();
      },
    });
  }

  exportBusinessCentral(includeAllPrepayments: boolean): void {
    const xlsxType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    this.exporting.set(true);
    this.adminApi.getBusinessCentralExport(includeAllPrepayments, 'response', false, {httpHeaderAccept: xlsxType as any}).subscribe({
      next: (response: HttpResponse<any>) => {
        const url = window.URL.createObjectURL(new Blob([response.body], {type: xlsxType}));
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        const filenameMatch = disposition?.match(/filename="([^"]+)"|filename=([^;\s]+)/);
        a.download = filenameMatch?.[1] ?? filenameMatch?.[2] ?? 'BusinessCentral_Export.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        this.exporting.set(false);
      },
      error: err => {
        console.error('Business Central export failed', err);
        this.errorMessageService.showTemporaryErrorWithTitle('Export Failed', 'The Business Central export could not be generated.', 8000);
        this.exporting.set(false);
      },
    });
  }

  resetSelectedTa(): void {
    this.store.resetTaState();
  }

  protected readonly financeTabs = FinanceDashboardTabOptions;
}
